import { ClientConfig } from '../configuration';
import { getIdpExchangeClientId, getIdpUrl, parseJwtExpiry, resolveEnv } from './idpEnv';

type CachedToken = { token: string; expiresAt: number };

type IdpHost = {
  on: (event: string, listener: (...args: unknown[]) => void) => void;
};

type IdpHttpClient = {
  getAuth: () => { key: string; secret: string };
};

/**
 * Single owner of the api_key → OAuth token exchange, shared by every caller
 * that needs an IDP bearer token (AgentCoreAPI, OrganizationsAPI).
 *
 * Created once on the client, so there is exactly one signedOut listener and
 * cleanup happens on dispose()/reset() — the cache is not parked in the
 * module-global CN session object.
 */
export default class IdpTokenProvider {
  // The IDP rejects tokens expiring within ~1 minute; use 65s so the cache
  // evicts them before the server does.
  private static readonly EXPIRY_BUFFER_MS = 65_000;

  // Cache key: `${organizationId}:${auth.key}:${scope}`. Including auth.key
  // makes a credential rotation a natural miss; including scope lets each
  // caller keep its own least-privilege token without evicting the other's.
  private cache = new Map<string, CachedToken>();

  constructor(
    private host: IdpHost,
    private httpClient: IdpHttpClient,
    private config: ClientConfig
  ) {
    this.host.on('signedOut', () => this.clear());
  }

  async getToken({
    organizationId,
    scope,
  }: {
    organizationId: string;
    scope: string;
  }): Promise<string> {
    const auth = this.httpClient.getAuth();
    if (!auth?.key || !auth?.secret) {
      throw new Error('API key/secret required to obtain IDP token');
    }

    const cacheKey = `${organizationId}:${auth.key}:${scope}`;
    const cached = this.cache.get(cacheKey);
    if (cached && cached.expiresAt - Date.now() > IdpTokenProvider.EXPIRY_BUFFER_MS) {
      return cached.token;
    }

    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    const response = await fetch(`${getIdpUrl(env)}/connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'api_key',
        api_key: auth.key,
        api_secret: auth.secret,
        client_id: getIdpExchangeClientId(env),
        org_id: organizationId,
        scope,
      }).toString(),
    });

    if (!response.ok) {
      throw new Error(`IDP token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    const token: string = data.access_token;
    // Cache only when the JWT exp claim is readable — otherwise always fetch fresh.
    const expiresAt = parseJwtExpiry(token);
    if (expiresAt !== null) {
      this.cache.set(cacheKey, { token, expiresAt });
    }
    return token;
  }

  /**
   * Evict cached tokens.
   * With an organizationId: drops all entries for that org (any credential/scope).
   * Without an argument: clears everything (used on sign-out).
   */
  clear(organizationId?: string): void {
    if (organizationId !== undefined) {
      const prefix = `${organizationId}:`;
      Array.from(this.cache.keys()).forEach((key) => {
        if (key.startsWith(prefix)) {
          this.cache.delete(key);
        }
      });
    } else {
      this.cache.clear();
    }
  }

  dispose(): void {
    this.cache.clear();
  }
}
